import { Box } from "@mui/system";
import {
  Button,
  IconButton,
  Skeleton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import PageTitle from "../PageTitle";
import { useContext, useEffect, useState } from "react";
import EnhancedTable from "../../layouts/table";
import FilterForm from "./FilterForm";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import axios from "axios";
import LoadingTableRows from "../LoadingTableRows";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";

interface IFilter {
  status: boolean;
  data: {
    full_name: string;
    wallet_address: string;
  };
}
const initFilterData: IFilter = {
  status: true,
  data: {
    full_name: "",
    wallet_address: "",
  },
};

export default function ClaimerManagement() {
  const { user } = useContext(AppContext)
  const { updateClaimers }: any = useOutletContext();
  const [tableData, setTableDatas] = useState({
    page: 1,
    pagesCount: 10,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Full Name",
      },
      {
        id: "address",
        numeric: false,
        disablePadding: false,
        label: "Wallet Address",
      },
      {
        id: "balance",
        numeric: false,
        disablePadding: false,
        label: "Balance",
      },
      {
        id: "total_claimed",
        numeric: false,
        disablePadding: false,
        label: "Total Claimed",
      },
      {
        id: "available_claim",
        numeric: false,
        disablePadding: false,
        label: "Total Available Claim",
      },
      {
        id: "blocked_claim",
        numeric: false,
        disablePadding: false,
        label: "Total Blocked",
      },
    ],
    tableItems: [],
  });
  const setTableData = (data: any) => setTableDatas({ ...tableData, ...data });
  const [order, setOrder] = useState({
    order: "asc",
    orderBy: "name",
  });
  const [statistics, setStatistics] = useState<any>({
    claimerLatestUpdate: "",
    presaleCompletion: "",
    spendPerCustomer: "",
    totalAvailableTokens: "",
    totalBuyers: "",
    totalClaimers: "",
    totalSales: "",
    totalSoldToken: "",
    totalSupply: "",
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<IFilter>(initFilterData);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTableData({
      rowsPerPage: Number(event.target.value),
      page: 1,
    });

    getClaimerList({
      name: filter.data.full_name,
      wallet_address: filter.data.wallet_address,
      ...(order.order ? {
        sortBy: order.order,
        sortOrder: order.orderBy
      } : {}),
      page: 1,
      perPage: event.target.value,
    });
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    getClaimerList({
      name: filter.data.full_name,
      wallet_address: filter.data.wallet_address,
      ...(order.order ? {
        sortBy: order.order,
        sortOrder: order.orderBy
      } : {}),
      page: newPage,
      perPage: tableData.rowsPerPage,
    });

    setTableData({ page: newPage });
  };

  const getClaimerList = async (params?: any) => {
    const {
      data: { data },
    } = await axios.get("/claimers/get-claimers", { params });

    setTableData({
      tableItems: data.data,
      pagesCount: data.total,
      rowsPerPage: data.per_page,
      page: data.current_page,
    });
  };

  const getTokenStatistics = async () => {
    const {
      data: { data },
    } = await axios.get("/claimers/get-token-statistics");

    setStatistics(data);
  };

  const getInitData = async () => {
    if (!user.permissions.includes("view_claimers")) return;
    await getClaimerList();
    await getTokenStatistics();

    setLoading(false);
  };

  useEffect(() => {
    getInitData();
  }, []);

  if (!user.permissions.includes("view_claimers")) return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <h3>You dont have permissions to view this page</h3>
    </Box>
  )

  return (
    <>
      <PageTitle
        title="Claimer Management"
        description="Manage your Claimers"
        leftElement={
          loading ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" color="textPrimary">
                Last time updated:{" "}
              </Typography>
              <Skeleton sx={{ minWidth: "70px", ml: 1 }} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="body2" color="textPrimary">
                Last time updated:{" "}
                <span style={{ fontWeight: "bold" }}>
                  {statistics.claimerLatestUpdate}
                </span>
              </Typography>
              <Button variant="outlined" onClick={updateClaimers} disabled={!user.permissions.includes("update_claimers")}>Update claimers</Button>
            </Box>
          )
        }
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {[
            { label: "Total Supply", key: "totalSupply" },
            { label: "Total Sold Tokens", key: "totalSoldToken" },
            { label: "Total Available Tokens", key: "totalAvailableTokens" },
            { label: "Total Sale", key: "totalSales" },
            { label: "Total Claimers", key: "totalClaimers" },
            { label: "Total Buyers", key: "totalBuyers" },
            { label: "Spend Per Customer", key: "spendPerCustomer" },
            {
              label: "Presale Completion %",
              key: "presaleCompletion",
            },
          ].map((claimer: any, index: number) => (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: "1 1 290px",
                border: "1px solid rgba(72, 94, 144, 0.16)",
                p: 2,
              }}
              key={index}
            >
              <Typography variant="subtitle2" color="textPrimary">
                {claimer.label}
              </Typography>
              {loading ? (
                <Skeleton />
              ) : (
                <Typography variant="h6">{statistics[claimer.key]}</Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ p: 3 }}>
        <EnhancedTable
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleRequestSortHeader={(orderFunc, orderByFunc) => {
            if (!orderByFunc) return;

            getClaimerList({
              name: filter.data.full_name,
              wallet_address: filter.data.wallet_address,
              sortBy: orderByFunc,
              sortOrder: orderFunc,
              page: 1,
              perPage: tableData.rowsPerPage,
            });

            setOrder({
              order: orderByFunc,
              orderBy: orderFunc,
            });
          }}
          TableRows={
            loading ? (
              <LoadingTableRows elementsCount={tableData.tableHead.length} />
            ) : (
              tableData.tableItems.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.address}</TableCell>
                  <TableCell>{row.balance}</TableCell>
                  <TableCell>{row.total_claimed}</TableCell>
                  <TableCell>{row.available_claim}</TableCell>
                  <TableCell>{row.blocked_claim}</TableCell>
                </TableRow>
              ))
            )
          }
          {...tableData}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() =>
                  setFilter({
                    ...filter,
                    status: !filter.status,
                  })
                }
              >
                {filter.status ? <FilterListIcon /> : <FilterListOffIcon />}
              </IconButton>
              <Typography variant="h6">Filter by</Typography>
            </Box>
            {filter.status && (
              <FilterForm
                filter={filter}
                setFilter={setFilter}
                handleSearch={() => {
                  getClaimerList({
                    name: filter.data.full_name,
                    wallet_address: filter.data.wallet_address,
                    sortBy: order.orderBy,
                    sortOrder: order.order,
                  });
                }}
                handleCancel={getClaimerList}
              />
            )}
          </Box>
        </EnhancedTable>
      </Box>
    </>
  );
}
